// i18n fragment - populated by the Phase 6 sweep. See ../strings.ts.
// App shell, primary nav, account + workspace switcher, command palette.
// App.vue / AppShell.vue carry no user-facing strings (layout only), so the
// app.* namespace stays empty; nav.* and palette.* hold the surfaced copy.
import type { LocaleStrings } from './base';

export const shellStrings: LocaleStrings = {
  en: {
    // ── Primary nav links (apps/web-ui/src/components/AppNav.vue) ──
    'nav.member.home':                'Home',
    'nav.member.inbox':               'Inbox',
    'nav.member.studio':              'Studio',
    'nav.member.files':               'Files',
    'nav.member.apps':                'Apps',
    'nav.member.tables':              'Tables',
    'nav.member.schedules':           'Schedules',
    'nav.member.activity':            'Activity',
    'nav.member.email_replies':       'Email replies',
    'nav.admin.agents':               'Agents',
    'nav.admin.integrations':         'Integrations',
    'nav.admin.channels':             'Channels',
    'nav.admin.email':                'Email',
    'nav.admin.skills':               'Skills',
    'nav.admin.business_profile':     'Business profile',
    'nav.admin.connect':              'Connect',
    'nav.admin.workspace_schedules':  'Workspace schedules',
    'nav.admin.audit':                'Audit',
    'nav.admin.users':                'Users',
    'nav.admin.usage':                'Usage & billing',
    'nav.admin.settings':             'Settings',
    'nav.landmark':                   'Primary',
    'nav.divider.admin':              'Admin',
    'nav.divider.platform':           'Platform',
    'nav.operator':                   'Operator',
    'nav.help':                       'Help & docs',
    'nav.search.label':               'Search',
    'nav.search.tooltip':             'Search ({kbd})',
    'nav.badge.unread':               'unread',
    'nav.badge.needs_attention':      'needs attention',
    'nav.collapse.expand':            'Expand menu',
    'nav.collapse.collapse':          'Collapse menu',
    'nav.menu.open':                  'Open menu',
    'nav.menu.close':                 'Close menu',

    // Workspace switcher (the seal row + its menu).
    'nav.ws.switch':                  'Switch workspace',
    'nav.ws.heading':                 'Workspaces',
    'nav.ws.add':                     'Add a workspace',
    'nav.ws.fallback_name':           'Workspace',
    'nav.ws.switch_error':            'Could not switch workspace',

    // Account menu (the bottom user card).
    'nav.account.label':              'Account',
    'nav.account.profile':            'Profile',
    'nav.account.developer_apps':     'Developer apps',
    'nav.account.light_theme':        'Light theme',
    'nav.account.dark_theme':         'Dark theme',
    'nav.account.sign_out':           'Sign out',

    // ── Command palette (apps/web-ui/src/components/CommandPalette.vue) ──
    'palette.placeholder':            'Search or run a command…',
    'palette.action.quick_ask':       'Quick ask',
    'palette.action.quick_ask_hint':  'Instant answer',
    'palette.action.new_thread':      'New thread',
    'palette.action.new_thread_hint': 'Pick an agent',
    'palette.action.switch_light':    'Switch to light theme',
    'palette.action.switch_dark':     'Switch to dark theme',
    'palette.group.actions':          'Actions',
    'palette.group.agents':           'Agents',
    'palette.group.goto':             'Go to',
    'palette.group.switch_workspace': 'Switch workspace',
    'palette.group.account':          'Account',
    'palette.group.conversations':    'Conversations',
    'palette.message_agent':          'Message {name}',
    'palette.goto.notifications':     'Notifications',
    'palette.goto.operator_console':  'Operator console',
    'palette.account.sign_out':       'Sign out',
    'palette.searching':              'Searching…',
    'palette.untitled_conversation':  'Untitled conversation',
    'palette.err.switch_workspace':   'Could not switch workspace',
    'palette.err.start_conversation': 'Could not start that conversation.',
  },
  sv: {
    // ── Primary nav links (apps/web-ui/src/components/AppNav.vue) ──
    'nav.member.home':                'Hem',
    'nav.member.inbox':               'Inkorg',
    'nav.member.studio':              'Studio',
    'nav.member.files':               'Filer',
    'nav.member.apps':                'Appar',
    'nav.member.tables':              'Tabeller',
    'nav.member.schedules':           'Scheman',
    'nav.member.activity':            'Aktivitet',
    'nav.member.email_replies':       'E-postsvar',
    'nav.admin.agents':               'Agenter',
    'nav.admin.integrations':         'Integrationer',
    'nav.admin.channels':             'Kanaler',
    'nav.admin.email':                'E-post',
    'nav.admin.skills':               'Färdigheter',
    'nav.admin.business_profile':     'Företagsprofil',
    'nav.admin.connect':              'Anslut',
    'nav.admin.workspace_schedules':  'Arbetsytans scheman',
    'nav.admin.audit':                'Granskning',
    'nav.admin.users':                'Användare',
    'nav.admin.usage':                'Användning & fakturering',
    'nav.admin.settings':             'Inställningar',
    'nav.landmark':                   'Primär',
    'nav.divider.admin':              'Admin',
    'nav.divider.platform':           'Plattform',
    'nav.operator':                   'Operatör',
    'nav.help':                       'Hjälp & dokumentation',
    'nav.search.label':               'Sök',
    'nav.search.tooltip':             'Sök ({kbd})',
    'nav.badge.unread':               'olästa',
    'nav.badge.needs_attention':      'kräver åtgärd',
    'nav.collapse.expand':            'Expandera menyn',
    'nav.collapse.collapse':          'Fäll ihop menyn',
    'nav.menu.open':                  'Öppna menyn',
    'nav.menu.close':                 'Stäng menyn',

    // Workspace switcher (the seal row + its menu).
    'nav.ws.switch':                  'Byt arbetsyta',
    'nav.ws.heading':                 'Arbetsytor',
    'nav.ws.add':                     'Lägg till en arbetsyta',
    'nav.ws.fallback_name':           'Arbetsyta',
    'nav.ws.switch_error':            'Det gick inte att byta arbetsyta',

    // Account menu (the bottom user card).
    'nav.account.label':              'Konto',
    'nav.account.profile':            'Profil',
    'nav.account.developer_apps':     'Utvecklarappar',
    'nav.account.light_theme':        'Ljust tema',
    'nav.account.dark_theme':         'Mörkt tema',
    'nav.account.sign_out':           'Logga ut',

    // ── Command palette (apps/web-ui/src/components/CommandPalette.vue) ──
    'palette.placeholder':            'Sök eller kör ett kommando…',
    'palette.action.quick_ask':       'Snabbfråga',
    'palette.action.quick_ask_hint':  'Direkt svar',
    'palette.action.new_thread':      'Ny tråd',
    'palette.action.new_thread_hint': 'Välj en agent',
    'palette.action.switch_light':    'Byt till ljust tema',
    'palette.action.switch_dark':     'Byt till mörkt tema',
    'palette.group.actions':          'Åtgärder',
    'palette.group.agents':           'Agenter',
    'palette.group.goto':             'Gå till',
    'palette.group.switch_workspace': 'Byt arbetsyta',
    'palette.group.account':          'Konto',
    'palette.group.conversations':    'Konversationer',
    'palette.message_agent':          'Skriv till {name}',
    'palette.goto.notifications':     'Aviseringar',
    'palette.goto.operator_console':  'Operatörskonsol',
    'palette.account.sign_out':       'Logga ut',
    'palette.searching':              'Söker…',
    'palette.untitled_conversation':  'Namnlös konversation',
    'palette.err.switch_workspace':   'Det gick inte att byta arbetsyta',
    'palette.err.start_conversation': 'Det gick inte att starta den konversationen.',
  },
};
